import React, { useEffect, useState, useMemo } from "react";
import "./HistoryView.css";
import { listenToAcceptedRequests } from "../services/simpleFirebase";
import { formatMeetingDateLong, formatMeetingDateTime } from "../utils/dateHelpers";
import jsPDF from "jspdf";

const HistoryView = ({ onBack }) => {
  const [acceptedRequests, setAcceptedRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStats, setShowStats] = useState(true);

  useEffect(() => {
    console.log("📚 [HistoryView] Démarrage de l'écoute des demandes acceptées");
    
    const unsubscribe = listenToAcceptedRequests((requests) => {
      console.log("📚 [HistoryView] Demandes acceptées reçues:", requests.length);
      setAcceptedRequests(requests);
      setLoading(false);
    });

    return () => {
      console.log("📚 [HistoryView] Nettoyage de l'écoute");
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Trier les demandes par date (plus récentes en premier)
  const sortedRequests = useMemo(() => {
    return [...acceptedRequests].sort((a, b) => {
      const dateA = a.assignedAt || a.createdAt || 0;
      const dateB = b.assignedAt || b.createdAt || 0;
      return dateB - dateA;
    });
  }, [acceptedRequests]);

  // Grouper par date de réunion
  const groupedByDate = useMemo(() => {
    const groups = {};
    
    sortedRequests.forEach((request) => {
      const meetingDate = request.meetingAt 
        ? new Date(request.meetingAt).toISOString().split("T")[0]
        : "Date inconnue";
      
      if (!groups[meetingDate]) {
        groups[meetingDate] = [];
      }
      groups[meetingDate].push(request);
    });

    // Trier les dates (plus récentes en premier)
    const sortedDates = Object.keys(groups).sort((a, b) => {
      if (a === "Date inconnue") return 1;
      if (b === "Date inconnue") return -1;
      return new Date(b) - new Date(a);
    });

    return sortedDates.map((date) => ({
      date,
      requests: groups[date],
    }));
  }, [sortedRequests]);

  // Calcul des statistiques
  const statistics = useMemo(() => {
    if (acceptedRequests.length === 0) {
      return {
        totalRequests: 0,
        totalPlaces: 0,
        uniqueDrivers: 0,
        uniquePassengers: 0,
        firstRequestDate: null,
        lastRequestDate: null,
        byDriver: {},
        byDayOfWeek: { mercredi: 0, samedi: 0 },
        byMonth: {},
      };
    }

    const drivers = new Set();
    const passengers = new Set();
    let totalPlaces = 0;
    const byDriver = {};
    const byMonth = {};
    const byDayOfWeek = { mercredi: 0, samedi: 0 };
    let firstDate = Infinity;
    let lastDate = 0;

    acceptedRequests.forEach((request) => {
      // Conducteurs
      if (request.assignedTo) {
        drivers.add(request.assignedTo);
        byDriver[request.assignedTo] = (byDriver[request.assignedTo] || 0) + 1;
      }

      // Passagers
      if (request.name) {
        passengers.add(request.name);
      }

      // Places
      totalPlaces += request.seatsNeeded || 1;

      // Dates
      const assignedAt = request.assignedAt || request.createdAt || 0;
      if (assignedAt > 0) {
        firstDate = Math.min(firstDate, assignedAt);
        lastDate = Math.max(lastDate, assignedAt);
      }

      // Par jour de la semaine
      if (request.meetingAt) {
        const meetingDate = new Date(request.meetingAt);
        const dayOfWeek = meetingDate.getDay(); // 0 = dimanche, 3 = mercredi, 6 = samedi
        if (dayOfWeek === 3) {
          byDayOfWeek.mercredi = (byDayOfWeek.mercredi || 0) + 1;
        } else if (dayOfWeek === 6) {
          byDayOfWeek.samedi = (byDayOfWeek.samedi || 0) + 1;
        }
      }

      // Par mois
      if (request.meetingAt) {
        const meetingDate = new Date(request.meetingAt);
        const monthKey = `${meetingDate.getFullYear()}-${String(meetingDate.getMonth() + 1).padStart(2, "0")}`;
        byMonth[monthKey] = (byMonth[monthKey] || 0) + 1;
      }
    });

    // Top conducteurs (trié)
    const topDrivers = Object.entries(byDriver)
      .filter(([name]) => name !== "mercredi" && name !== "samedi")
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    return {
      totalRequests: acceptedRequests.length,
      totalPlaces,
      uniqueDrivers: drivers.size,
      uniquePassengers: passengers.size,
      firstRequestDate: firstDate !== Infinity ? new Date(firstDate) : null,
      lastRequestDate: lastDate > 0 ? new Date(lastDate) : null,
      byDriver,
      topDrivers,
      byDayOfWeek,
      byMonth,
    };
  }, [acceptedRequests]);

  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const lineHeight = 7;
    let yPosition = margin;

    // Fonction pour ajouter une nouvelle page si nécessaire
    const checkNewPage = (requiredSpace) => {
      if (yPosition + requiredSpace > pageHeight - margin) {
        doc.addPage();
        yPosition = margin;
      }
    };

    // Titre principal
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("📖 Historique des requêtes acceptées", pageWidth / 2, yPosition, {
      align: "center"
    });
    yPosition += lineHeight * 2;

    // Date de génération
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const now = new Date();
    const dateStr = now.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
    doc.text(`Généré le ${dateStr}`, pageWidth / 2, yPosition, {
      align: "center"
    });
    yPosition += lineHeight * 2;

    // Statistiques
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(
      `Total : ${acceptedRequests.length} demande${acceptedRequests.length > 1 ? "s" : ""} acceptée${acceptedRequests.length > 1 ? "s" : ""}`,
      margin,
      yPosition
    );
    yPosition += lineHeight * 2;

    // Parcourir les groupes par date
    groupedByDate.forEach(({ date, requests }, groupIndex) => {
      checkNewPage(lineHeight * 3);

      // Titre de la date
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      const dateTitle = date === "Date inconnue" 
        ? "Date inconnue" 
        : formatMeetingDateLong(new Date(date).getTime());
      doc.text(dateTitle, margin, yPosition);
      yPosition += lineHeight * 1.5;

      // Ligne de séparation
      doc.setLineWidth(0.5);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += lineHeight;

      // Parcourir les requêtes de cette date
      requests.forEach((request, requestIndex) => {
        checkNewPage(lineHeight * 5);

        // Nom
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.text(
          `${requestIndex + 1}. ${request.name || "Nom non renseigné"}`,
          margin + 5,
          yPosition
        );
        yPosition += lineHeight;

        // Conducteur
        if (request.assignedTo) {
          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          doc.text(`   Conducteur : ${request.assignedTo}`, margin + 5, yPosition);
          yPosition += lineHeight;
        }

        // Adresse
        if (request.address) {
          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          const addressLines = doc.splitTextToSize(`   Adresse : ${request.address}`, pageWidth - margin * 2 - 10);
          doc.text(addressLines, margin + 5, yPosition);
          yPosition += lineHeight * addressLines.length;
        }

        // Détails (places et date d'acceptation)
        doc.setFontSize(9);
        doc.setFont("helvetica", "italic");
        let details = [];
        if (request.seatsNeeded) {
          details.push(`${request.seatsNeeded} place${request.seatsNeeded > 1 ? "s" : ""}`);
        }
        if (request.assignedAt) {
          details.push(`Acceptée le ${formatMeetingDateTime(request.assignedAt)}`);
        }
        if (details.length > 0) {
          doc.text(`   ${details.join(" • ")}`, margin + 5, yPosition);
          yPosition += lineHeight;
        }

        // Espacement entre les requêtes
        yPosition += lineHeight * 0.5;
      });

      // Espacement entre les groupes de dates
      if (groupIndex < groupedByDate.length - 1) {
        yPosition += lineHeight;
      }
    });

    // Nom du fichier avec date
    const fileName = `historique-covoiturage-${now.toISOString().split("T")[0]}.pdf`;
    doc.save(fileName);
  };

  if (loading) {
    return (
      <div className="history-view">
        <div className="history-header">
          <button className="history-back-button" onClick={onBack}>
            ← Retour
          </button>
          <h1>📖 Historique des requêtes</h1>
        </div>
        <div className="history-loading">
          <p>Chargement de l'historique...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="history-view">
      <div className="history-header">
        <button className="history-back-button" onClick={onBack}>
          ← Retour
        </button>
        <h1>📖 Historique des requêtes</h1>
        <div className="history-header-right">
          <div className="history-count">
            {acceptedRequests.length} demande{acceptedRequests.length > 1 ? "s" : ""} acceptée{acceptedRequests.length > 1 ? "s" : ""}
          </div>
          {acceptedRequests.length > 0 && (
            <>
              <button
                className="history-stats-toggle"
                onClick={() => setShowStats(!showStats)}
                title={showStats ? "Masquer les statistiques" : "Afficher les statistiques"}
              >
                {showStats ? "📊" : "📈"}
              </button>
              <button className="history-pdf-button" onClick={generatePDF} title="Télécharger en PDF">
                📄 PDF
              </button>
            </>
          )}
        </div>
      </div>

      {acceptedRequests.length > 0 && showStats && (
        <div className="history-statistics">
          <h2 className="history-statistics-title">📊 Statistiques</h2>
          <div className="history-statistics-grid">
            <div className="stat-card stat-card-primary">
              <div className="stat-icon">🚗</div>
              <div className="stat-value">{statistics.totalRequests}</div>
              <div className="stat-label">Demandes acceptées</div>
            </div>

            <div className="stat-card stat-card-success">
              <div className="stat-icon">💺</div>
              <div className="stat-value">{statistics.totalPlaces}</div>
              <div className="stat-label">Places transportées</div>
            </div>

            <div className="stat-card stat-card-info">
              <div className="stat-icon">👥</div>
              <div className="stat-value">{statistics.uniqueDrivers}</div>
              <div className="stat-label">Conducteurs actifs</div>
            </div>

            <div className="stat-card stat-card-warning">
              <div className="stat-icon">👤</div>
              <div className="stat-value">{statistics.uniquePassengers}</div>
              <div className="stat-label">Passagers uniques</div>
            </div>
          </div>

          {(statistics.byDayOfWeek.mercredi > 0 || statistics.byDayOfWeek.samedi > 0) && (
            <div className="history-statistics-section">
              <h3 className="history-statistics-subtitle">📅 Répartition par jour</h3>
              <div className="day-distribution">
                <div className="day-item">
                  <span className="day-label">Mercredi</span>
                  <div className="day-bar-container">
                    <div
                      className="day-bar day-bar-mercredi"
                      style={{
                        width: `${
                          statistics.totalRequests > 0
                            ? (statistics.byDayOfWeek.mercredi / statistics.totalRequests) * 100
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                  <span className="day-count">{statistics.byDayOfWeek.mercredi}</span>
                </div>
                <div className="day-item">
                  <span className="day-label">Samedi</span>
                  <div className="day-bar-container">
                    <div
                      className="day-bar day-bar-samedi"
                      style={{
                        width: `${
                          statistics.totalRequests > 0
                            ? (statistics.byDayOfWeek.samedi / statistics.totalRequests) * 100
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                  <span className="day-count">{statistics.byDayOfWeek.samedi}</span>
                </div>
              </div>
            </div>
          )}

          {(statistics.firstRequestDate || statistics.lastRequestDate) && (
            <div className="history-statistics-section">
              <h3 className="history-statistics-subtitle">📆 Période</h3>
              <div className="period-info">
                {statistics.firstRequestDate && (
                  <div className="period-item">
                    <span className="period-label">Première demande :</span>
                    <span className="period-value">
                      {statistics.firstRequestDate.toLocaleDateString("fr-FR", {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                )}
                {statistics.lastRequestDate && (
                  <div className="period-item">
                    <span className="period-label">Dernière demande :</span>
                    <span className="period-value">
                      {statistics.lastRequestDate.toLocaleDateString("fr-FR", {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {groupedByDate.length === 0 ? (
        <div className="history-empty">
          <p>📭 Aucune demande acceptée pour le moment</p>
        </div>
      ) : (
        <div className="history-content">
          {groupedByDate.map(({ date, requests }) => (
            <div key={date} className="history-date-group">
              <h2 className="history-date-title">
                {date === "Date inconnue" 
                  ? "Date inconnue" 
                  : formatMeetingDateLong(new Date(date).getTime())}
              </h2>
              <div className="history-requests-list">
                {requests.map((request) => (
                  <div key={request.id} className="history-request-card">
                    <div className="history-request-header">
                      <span className="history-request-name">
                        {request.name || "Nom non renseigné"}
                      </span>
                      {request.assignedTo && (
                        <span className="history-request-driver">
                          👤 {request.assignedTo}
                        </span>
                      )}
                    </div>
                    
                    {request.address && (
                      <div className="history-request-address">
                        📍 {request.address}
                      </div>
                    )}

                    <div className="history-request-details">
                      {request.seatsNeeded && (
                        <span className="history-request-seats">
                          💺 {request.seatsNeeded} place{request.seatsNeeded > 1 ? "s" : ""}
                        </span>
                      )}
                      {request.assignedAt && (
                        <span className="history-request-date">
                          ✅ Acceptée le {formatMeetingDateTime(request.assignedAt)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HistoryView;

